import React from "react";
import OrangeScoreBadge from "@/hq/OrangeScoreBadge";

/**
 * OptimiseExplainer — content sections that appear BELOW the /optimise
 * scan hero. Preserves the hero above. Uses the existing OrangeBySite
 * design system (orange/black/white, canonical OrangeScoreBadge).
 */

const CATS = [
  { n: "01", key: "performance", label: "Performance",
    copy: "Loading behaviour, responsiveness and public-facing performance indicators." },
  { n: "02", key: "mobile",      label: "Mobile",
    copy: "How well the website behaves and presents itself across mobile devices." },
  { n: "03", key: "assets",      label: "Assets & images",
    copy: "Image and asset efficiency and areas that may be unnecessarily slowing the site." },
  { n: "04", key: "technical",   label: "Technical",
    copy: "Public-facing technical setup and detectable configuration issues." },
  { n: "05", key: "security",    label: "Security health",
    copy: "Externally detectable security-health signals and configuration weaknesses." },
  { n: "06", key: "efficiency",  label: "Efficiency",
    copy: "General website efficiency and opportunities for improvement." },
];

const STEPS = [
  { title: "Your website",          copy: "You give us the address — nothing else." },
  { title: "Free health scan",      copy: "Orange Optimise checks it publicly, in seconds." },
  { title: "Orange Score",          copy: "One clear score from 0 to 100." },
  { title: "Issues identified",     copy: "Everything worth knowing, plain-English." },
  { title: "Recommendations",       copy: "What to fix first for the biggest win." },
  { title: "Keep monitoring",       copy: "Optional — Orange Optimise watches over time." },
];

export default function OptimiseExplainer() {
  return (
    <div className="mt-24 space-y-24" data-testid="optimise-explainer">

      {/* ============ How Orange Optimise works ============ */}
      <section data-testid="optimise-how-it-works">
        <div className="text-[10px] tracking-[0.32em] uppercase font-black text-[#FF6A00]">How it works</div>
        <h2 className="mt-2 font-black text-3xl md:text-5xl tracking-[-0.03em]">
          From your website to a clear result — in seconds.
        </h2>

        <ol className="mt-10 grid grid-cols-1 md:grid-cols-6 gap-4">
          {STEPS.map((s, i) => (
            <li key={s.title}
                data-testid={`optimise-step-${i+1}`}
                className="relative rounded-2xl border-2 border-black/10 bg-white p-4 md:p-5">
              <div className="text-[10px] tracking-[0.22em] uppercase font-black text-[#FF6A00]">
                Step {String(i+1).padStart(2, "0")}
              </div>
              <div className="mt-1.5 font-black text-[16px] tracking-[-0.01em]">{s.title}</div>
              <p className="mt-1.5 text-[12px] text-black/70 leading-relaxed">{s.copy}</p>
              {/* Desktop arrow */}
              {i < STEPS.length - 1 && (
                <div aria-hidden
                     className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 text-2xl font-black text-[#FF6A00]">
                  →
                </div>
              )}
              {/* Mobile arrow */}
              {i < STEPS.length - 1 && (
                <div aria-hidden className="md:hidden mt-3 text-center text-xl font-black text-[#FF6A00]">
                  ↓
                </div>
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* ============ What we check ============ */}
      <section data-testid="optimise-what-we-check">
        <div className="text-[10px] tracking-[0.32em] uppercase font-black text-[#FF6A00]">What we check</div>
        <h2 className="mt-2 font-black text-3xl md:text-5xl tracking-[-0.03em]">
          Six things, one clear score.
        </h2>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {CATS.map((c) => (
            <div key={c.key}
                 data-testid={`optimise-category-${c.key}`}
                 className="rounded-3xl border-2 border-black bg-white p-6 hover:-translate-y-0.5 transition-transform">
              <div className="text-[10px] tracking-[0.22em] uppercase font-black text-[#FF6A00]">
                {c.n}
              </div>
              <h3 className="mt-1 font-black text-2xl tracking-[-0.02em]">{c.label}</h3>
              <p className="mt-3 text-[14px] text-black/70 leading-relaxed">{c.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ Visual scan diagram ============ */}
      <section data-testid="optimise-scan-diagram" className="text-center">
        <style>{`
          @media (prefers-reduced-motion: no-preference) {
            .obs-scan-diag > * { opacity: 0; transform: translateY(6px);
              animation: obs-scan-diag-in 400ms ease-out both; }
            .obs-scan-diag > *:nth-child(1) { animation-delay: 0ms; }
            .obs-scan-diag > *:nth-child(2) { animation-delay: 80ms; }
            .obs-scan-diag > *:nth-child(3) { animation-delay: 160ms; }
            .obs-scan-diag > *:nth-child(4) { animation-delay: 240ms; }
            .obs-scan-diag > *:nth-child(5) { animation-delay: 320ms; }
            .obs-scan-diag > *:nth-child(6) { animation-delay: 400ms; }
            .obs-scan-diag > *:nth-child(7) { animation-delay: 480ms; }
            @keyframes obs-scan-diag-in { to { opacity:1; transform:none; } }
          }
        `}</style>
        <div className="text-[10px] tracking-[0.32em] uppercase font-black text-[#FF6A00]">Inside a scan</div>
        <h2 className="mt-2 font-black text-3xl md:text-5xl tracking-[-0.03em]">
          What actually happens.
        </h2>

        <div className="obs-scan-diag mt-12 max-w-2xl mx-auto flex flex-col items-center gap-4">
          <DiagChip label="Your website" />
          <DiagArrow />
          <DiagChip label="Orange Optimise health scan" filled />
          <DiagArrow />
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            {CATS.map((c) => (
              <div key={c.key}
                   className="rounded-full border border-black/20 bg-white px-3 py-2 text-[10px] tracking-[0.14em] uppercase font-black">
                {c.label}
              </div>
            ))}
          </div>
          <DiagArrow />
          <div className="flex items-center gap-3">
            <OrangeScoreBadge score={87} size="lg" testId="optimise-diagram-score"/>
            <div className="font-black text-2xl tracking-[-0.02em]">Orange Score</div>
          </div>
          <DiagArrow />
          <DiagChip label="Clear recommendations" filled />
        </div>
      </section>

      {/* ============ Your Orange Score ============ */}
      <section data-testid="optimise-score-explainer">
        <div className="text-[10px] tracking-[0.32em] uppercase font-black text-[#FF6A00]">Your Orange Score</div>
        <h2 className="mt-2 font-black text-3xl md:text-5xl tracking-[-0.03em]">
          The healthier your website, the closer you get to an Orange.
        </h2>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { score: 68, range: "0 – 79",  band: "Tomato",       hint: "Room to grow — usually a handful of quick wins." },
            { score: 84, range: "80 – 89", band: "Banana bunch", hint: "Healthy — a few tweaks and you're nearly there." },
            { score: 94, range: "90 – 100", band: "Orange",      hint: "Excellent — keep it that way with monitoring." },
          ].map((row) => (
            <div key={row.band}
                 data-testid={`optimise-score-band-${row.band.split(" ")[0].toLowerCase()}`}
                 className="rounded-3xl border-2 border-black/10 bg-white p-6 flex flex-col items-center text-center">
              <OrangeScoreBadge score={row.score} size="lg"/>
              <div className="mt-4 text-[10px] tracking-[0.22em] uppercase font-black text-[#FF6A00]">
                {row.range}
              </div>
              <div className="mt-1 font-black text-xl tracking-[-0.01em]">{row.band}</div>
              <p className="mt-2 text-[13px] text-black/70">{row.hint}</p>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
}

function DiagChip({ label, filled }) {
  return (
    <div className={`rounded-2xl px-5 py-3 text-[11px] tracking-[0.22em] uppercase font-black border-2 ${
      filled ? "bg-black text-white border-black" : "bg-white text-black border-black"
    }`}>
      {label}
    </div>
  );
}
function DiagArrow() {
  return <div aria-hidden className="text-2xl font-black text-[#FF6A00]">↓</div>;
}
